import { ENDPOINTS } from "../utils/appConfig";
import { fetchWithAuthRetry, getAuthHeaders } from "../utils/helpers";
import { transformLLMResponse } from "../utils/llmTransformer";
import { hasDomain } from "../utils/authStorage";

export async function fetchAlertDetails(alertId) {
  if (!hasDomain('surveillance')) return null;
  const response = await fetchWithAuthRetry(ENDPOINTS.ALERT_DETAILS(alertId), {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return {
    ...data,
    llm_response: transformLLMResponse(data.llm_response)
  };
}

export async function fetchAlertsList() {
  if (!hasDomain('surveillance')) return [];
  const response = await fetchWithAuthRetry(ENDPOINTS.ALERT_LIST, {
    method: "GET",
    headers: getAuthHeaders()
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return data || [];
}

export async function fetchActiveAlerts() {
  if (!hasDomain('surveillance')) return [];
  const res = await fetchWithAuthRetry(ENDPOINTS.ACTIVE_ALERTS, {
    method: 'GET',
    headers: getAuthHeaders()
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();

  const alerts = (data.alerts_data || []).map(a => ({
    ...a,
    llm_response: transformLLMResponse(a.llm_response)
  }));

  const droneAlerts = (data.sensor_alerts_data || []).map(d => ({
    id: d.drone.id || d.drone.serial_number,
    is_drone: true,
    drone: { ...d.drone, drone_flight: d.drone_flight },
    is_active: d.drone.is_active,
    video_url: d.drone.video_url || d.drone.stream_url || null,
    created_at: d.drone.created_at || d.drone.updated_at || new Date().toISOString(),
  }));

  const allAlerts = [...alerts, ...droneAlerts];
  return normalizeStored(allAlerts);
}

export async function updateAlert(alertId, payload) {
  const response = await fetchWithAuthRetry(ENDPOINTS.ALERT_UPDATE(alertId), {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.detail || `Error ${response.status}: ${response.statusText}`);
  }
  return await response.json();
}

export async function closeAlert(alertId, isSensor, resolutionNotes) {
  const endpoint = isSensor ? ENDPOINTS.SENSOR_ALERT_CLOSE(alertId) : ENDPOINTS.ALERT_CLOSE(alertId);
  const response = await fetchWithAuthRetry(endpoint, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ resolution_notes: resolutionNotes }),
  });
  if (!response.ok) {
    const errData = await response.json().catch(() => null);
    throw new Error(errData?.detail || `Error ${response.status}: ${response.statusText}`);
  }
  return await response.json();
}

export async function dispatchAction(payload) {
  const response = await fetchWithAuthRetry(ENDPOINTS.DISPATCH_ACTION(), {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload)
  });
  if (!response.ok) throw new Error(`Dispatch failed: ${response.statusText}`);
  return await response.json();
}

export async function fetchSensorAlertDetails(sensorAlertId) {
  if (!hasDomain('surveillance')) return null;
  const response = await fetchWithAuthRetry(
    ENDPOINTS.SENSOR_ALERT_DETAILS(sensorAlertId),
    { method: "GET", headers: getAuthHeaders() }
  );
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);

  const sensorData = await response.json();
  let transformedLLM = null;

  if (sensorData.llm_response) {
    try {
      const parsedLLM = JSON.parse(sensorData.llm_response);
      transformedLLM = transformLLMResponse(parsedLLM);
    } catch (err) {
      console.warn("llm_response is not JSON, treating as plain text:", sensorData.llm_response);
      transformedLLM = {
        incident_category: sensorData.llm_response,
        observed_activity: [],
        risk_assessment: null,
        recommended_action: [],
        next_action: []
      };
    }
  }

  return {
    id: sensorData.sensor_alert,
    type: "sensor",
    sensor_id: sensorData.sensor_id,
    drone_id: sensorData.drone_id,
    drone_flight_id: sensorData.drone_flight_id,
    message: sensorData.description,
    start_time: sensorData.timestamp,
    alert_analysis: sensorData.status,
    title: sensorData.description || "Sensor Alert",
    severity: sensorData.severity,
    llm_response: transformedLLM,
    drone: sensorData?.drone ? { ...sensorData.drone } : null,
    devices: (sensorData?.devices || []).map(device => ({
      id: device?.device_id,
      device_name: device?.device_name,
      primary: device?.primary_device,
      status: device?.status,
      start_time: device?.recording_start,
      end_time: device?.recording_end,
      video_url: device?.video_url,
      device_type: device?.device_type,
    })),
    is_resolved: sensorData.is_resolved,
    resolved_at: sensorData.resolved_at,
    resolution_notes: sensorData.resolution_notes,
    resolved_by: sensorData.resolved_by
  };
}

export const normalizeStored = (arr) => arr.map(item => {
  if (item?.type === 'drone.alert' && item?.message) {
    const msg = item.message;
    return {
      id: msg.id || msg.serial_number || item.id,
      is_drone: true,
      drone: msg,
      is_active: msg.is_active !== undefined ? msg.is_active : true,
      video_url: msg.video_url || msg.stream_url || null,
      created_at: msg.created_at || msg.updated_at || new Date().toISOString(),
    };
  }
  if (item?.drone) {
    const d = item.drone;
    return {
      ...item,
      id: item.id || d.id || d.serial_number,
      is_drone: true,
      is_active: item.is_active !== undefined ? item.is_active : (d.is_active !== undefined ? d.is_active : true),
      video_url: item.video_url || d.video_url || d.stream_url || null,
      drone: d,
      type: "sensor",
    };
  }
  return {
    ...item,
    is_active: item.is_active !== undefined ? item.is_active : true,
  };
});

export async function fetchSensorAlerts() {
  if (!hasDomain('surveillance')) return [];
  const response = await fetchWithAuthRetry(ENDPOINTS.SENSOR_ALERTS, {
    method: "GET",
    headers: getAuthHeaders(),
  });
  if (!response.ok) throw new Error(`Error ${response.status}: ${response.statusText}`);
  const data = await response.json();
  return data || [];
}
